// src/components/sensors/useSensorLiveFeed.js
// FlowSense — live readings for ONE sensor straight from the WebSocket — NEW FILE
// ═══════════════════════════════════════════════════════════════════════════
// The details drawer used to depend on the page passing `liveReading`, which the
// page looked up by exact device-id case. This hook listens to the same events
// the page does, keeps only this device's messages (case-insensitive) and
// records when each one arrived. It subscribes only while `enabled` (drawer open).
// ═══════════════════════════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import webSocketService from '../../services/websocketService';
import { normalizeId, readingSignature, readingTimeMs } from './sensorLiveStatus';

const EVENTS = ['new_pressure_reading', 'new_water_level_reading', 'psi_update', 'telemetry'];
const EMPTY = Object.freeze({ latest: null, recent: [], arrivals: 0, lastArrivalMs: null, lastArrivalMeasuredMs: null });

/**
 * @param {string} deviceId
 * @param {{enabled?: boolean, max?: number}} [opts]
 * @returns {{latest: object|null, recent: object[], arrivals: number, lastArrivalMs: number|null, lastArrivalMeasuredMs: number|null}}
 *   recent: oldest → newest, each reading has `_arrivedAt` (ms, this browser)
 */
export function useSensorLiveFeed(deviceId, { enabled = true, max = 240 } = {}) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    setState(EMPTY);
    if (!enabled || !deviceId) return undefined;
    const key = normalizeId(deviceId);

    const handler = (message) => {
      const data = message && typeof message === 'object' ? message.payload || message.data || message : null;
      if (!data || typeof data !== 'object') return;
      const id = data.device_id ?? data.deviceId ?? data.device;
      if (normalizeId(id) !== key) return;
      const at = Date.now();
      setState((prev) => {
        const sig = readingSignature(data);
        if (prev.latest && readingSignature(prev.latest) === sig) return prev; // duplicate delivery
        const reading = { ...data, _arrivedAt: at };
        const measured = readingTimeMs(data);
        const prevMeasured = prev.latest ? readingTimeMs(prev.latest) : null;
        const newer = prevMeasured === null || measured === null || measured >= prevMeasured;
        return {
          latest: newer ? reading : prev.latest,
          recent: [...prev.recent, reading].slice(-max),
          arrivals: prev.arrivals + 1,
          lastArrivalMs: at,
          lastArrivalMeasuredMs: measured,
        };
      });
    };

    const offs = EVENTS.map((evt) => {
      const r = webSocketService.subscribe(evt, handler);
      return typeof r === 'function' ? r : () => webSocketService.unsubscribe(evt, handler);
    });
    return () => offs.forEach((off) => { try { off(); } catch (_) { /* already removed */ } });
  }, [deviceId, enabled, max]);

  return state;
}

export default useSensorLiveFeed;
